import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, callEdgeFunction } from './supabaseClient';
import { checkIsPlatformAdmin, isBaristaUser } from './authRoles';
import './CheckInScanPage.css';
import logo from './assets/newlogo.png';
import dukkan from './assets/dukkan2.jpg';

export default function CheckInScanPage() {
  const navigate = useNavigate();
  const { legacyToken, kioskMode } = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get('token') || '';
    const k = (sp.get('kiosk') || '').toLowerCase();
    const kiosk = k === '1' || k === 'true' || k === 'yes';
    return { legacyToken: token, kioskMode: kiosk };
  }, []);

  const officeCheckInUrl = useMemo(() => `${window.location.origin}/check-in`, []);
  const officeQrImage = useMemo(
    () =>
      `https://quickchart.io/qr?size=${kioskMode ? 480 : 280}&margin=2&text=${encodeURIComponent(officeCheckInUrl)}`,
    [officeCheckInUrl, kioskMode],
  );
  const legacyScannedUrl = useMemo(
    () => (legacyToken ? `${window.location.origin}/check-in?token=${legacyToken}` : ''),
    [legacyToken],
  );
  const legacyQrImage = useMemo(
    () => (legacyScannedUrl ? `https://quickchart.io/qr?size=130&text=${encodeURIComponent(legacyScannedUrl)}` : ''),
    [legacyScannedUrl],
  );

  const [result, setResult] = useState(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sessionUser, setSessionUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [adminSession, setAdminSession] = useState(false);

  useEffect(() => {
    if (!sessionUser) {
      setAdminSession(false);
      return;
    }
    checkIsPlatformAdmin(supabase, sessionUser).then(setAdminSession);
  }, [sessionUser]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSessionUser(data?.session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!legacyToken) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await callEdgeFunction('preview-employee-checkin-token', {
          method: 'POST',
          body: { p_token: legacyToken },
        });
        if (cancelled) return;
        setPreview(data);
      } catch (e) {
        if (cancelled) return;
        setPreview({ ok: false, reason: e?.message || 'Onizleme alinamadi.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legacyToken]);

  const lastConsumedTokenRef = useRef(null);

  useEffect(() => {
    lastConsumedTokenRef.current = null;
  }, [legacyToken]);

  const runConsumeLegacyToken = useCallback(async () => {
    if (!legacyToken || !preview?.ok) return;
    if (lastConsumedTokenRef.current === legacyToken) return;
    lastConsumedTokenRef.current = legacyToken;
    setAuthBusy(true);
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 180) : null;
    try {
      const data = await callEdgeFunction('consume-employee-checkin-token', {
        method: 'POST',
        body: { p_token: legacyToken, p_device: ua },
      });
      if (data && data.ok === false) {
        lastConsumedTokenRef.current = null;
      }
      setResult(data);
    } catch (e) {
      lastConsumedTokenRef.current = null;
      setResult({ ok: false, reason: e?.message || 'Check-in tamamlanamadi.' });
    } finally {
      setAuthBusy(false);
    }
  }, [legacyToken, preview]);

  const runRecordStaticCheckin = useCallback(async () => {
    setAuthBusy(true);
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 180) : null;
    try {
      const data = await callEdgeFunction('record-barista-checkin', {
        method: 'POST',
        body: { p_device: ua },
      });
      if (data && data.ok === false) {
        setResult(data);
        return;
      }
      setResult(data);
    } catch (e) {
      setResult({ ok: false, reason: e?.message || 'Check-in kaydedilemedi.' });
    } finally {
      setAuthBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionUser || !isBaristaUser(sessionUser)) return;
    if (!legacyToken || !preview?.ok) return;
    runConsumeLegacyToken();
  }, [sessionUser, legacyToken, preview?.ok, runConsumeLegacyToken]);

  const handleBaristaLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    const email = loginEmail.trim().toLowerCase();
    if (!email || !loginPassword) return;
    setLoginLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password: loginPassword });
    if (error) {
      setLoginLoading(false);
      setLoginError(error.message);
      return;
    }
    const user = data?.user;
    if (await checkIsPlatformAdmin(supabase, user)) {
      setLoginLoading(false);
      navigate('/dashboard', { replace: true });
      return;
    }
    if (isBaristaUser(user)) {
      setLoginPassword('');
      setLoginLoading(false);
      if (legacyToken && preview?.ok) {
        await runConsumeLegacyToken();
      } else if (!legacyToken) {
        await runRecordStaticCheckin();
      }
      return;
    }
    await supabase.auth.signOut();
    setLoginLoading(false);
    setLoginError('Bu hesap barista veya isletme sahibi (admin) olarak tanimli degil.');
  };

  const baristaSignedIn = sessionUser && isBaristaUser(sessionUser);
  const adminSignedIn = sessionUser && adminSession;
  const staticFlow = !legacyToken;

  const loginDisabled =
    loginLoading || authBusy || (legacyToken !== '' && preview === null);

  if (kioskMode && !legacyToken) {
    return (
      <div className="checkin-page checkin-page--kiosk">
        <div className="checkin-kiosk-wrap">
          <img src={logo} alt="" className="checkin-logo" />
          <h1 className="checkin-kiosk-title">Shift girisi</h1>
          <p className="checkin-kiosk-lead">Telefonunuzla QR kodu okutun — ardindan barista hesabinizla girin.</p>
          <div className="checkin-kiosk-qr">
            <img src={officeQrImage} alt="Magaza check-in QR" width={440} height={440} />
          </div>
          <p className="checkin-kiosk-foot">
            Bu ekrani ofis kioskunda acik tutun.
            <br />
            <span className="checkin-office-qr-url">{officeCheckInUrl}</span>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkin-page">
      <div className="checkin-content">
        <div className="checkin-left">
          <img src={logo} alt="" className="checkin-logo" />
          <h1 className="checkin-title">Shift girisi</h1>
          <p className="checkin-sub">
            {staticFlow
              ? 'Magaza QR ile geldiniz. Barista hesabinizla girin; giris kaydedilir.'
              : 'Eski tek kullanimlik QR (token). Gecerliyse giris ile tamamlanir.'}
          </p>
          <p className="checkin-welcome">Iyi vardiyalar.</p>

          {staticFlow ? (
            <div className="checkin-office-qr-block">
              <h2>Ofis ekrani — magaza QR</h2>
              <p className="checkin-office-qr-caption">
                Bu kodu tablet veya kasada gosterin; barista telefonuyla okutur. Ayni sayfayi tam ekran icin{' '}
                <Link to="/check-in?kiosk=1">/check-in?kiosk=1</Link> kullanin.
              </p>
              <div className="checkin-office-qr-frame">
                <img src={officeQrImage} alt="Check-in magaza QR" width={280} height={280} />
              </div>
              <div className="checkin-office-qr-url">{officeCheckInUrl}</div>
            </div>
          ) : null}

          {legacyToken ? (
            <div className="checkin-scanned">
              <img src={legacyQrImage} alt="" width={84} height={84} />
              <div>
                <div className="checkin-scanned-title">Eski QR</div>
                <div className="checkin-person-name">
                  {!preview
                    ? 'Yukleniyor...'
                    : preview.ok || preview.reason === 'already_used'
                      ? [preview.day, preview.shift_time].filter(Boolean).join(' · ') || '—'
                      : preview.reason === 'not_found'
                        ? 'QR gecersiz'
                        : '—'}
                </div>
                {preview?.ok ? (
                  <div className="checkin-scanned-meta" style={{ marginTop: '0.25rem' }}>
                    Giris icin asagidan oturum acin.
                  </div>
                ) : null}
                {preview?.reason === 'already_used' ? (
                  <div className="checkin-scanned-warn">Bu QR daha once kullanildi.</div>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="checkin-login-box">
            <h2>Barista girisi</h2>
            <p className="checkin-card-sub" style={{ marginBottom: 0 }}>
              {staticFlow
                ? 'E-posta ve sifrenizle giris yapin; shift girisiniz bu cihaz icin kaydedilir.'
                : 'Giris ile eski token QR islemi tamamlanir.'}
            </p>
            {adminSignedIn ? (
              <p className="checkin-session-pill">
                Isletme sahibi oturumu: <Link to="/dashboard">Yonetim paneli</Link>
              </p>
            ) : baristaSignedIn ? (
              <>
                <p className="checkin-session-pill">
                  Giris yapildi ({sessionUser.email}).{' '}
                  <Link to="/barista">Vardiyalarim</Link>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => supabase.auth.signOut()}
                    style={{
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      color: '#6e7468',
                      font: 'inherit',
                      fontWeight: 600,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Cikis
                  </button>
                </p>
                {staticFlow ? (
                  <button
                    type="button"
                    className="checkin-btn"
                    style={{ marginTop: 12 }}
                    disabled={authBusy}
                    onClick={() => runRecordStaticCheckin()}
                  >
                    {authBusy ? 'Kaydediliyor...' : 'Shift girisimi kaydet'}
                  </button>
                ) : null}
              </>
            ) : (
              <form className="checkin-login-fields" onSubmit={handleBaristaLogin}>
                <label>
                  E-posta
                  <input
                    type="email"
                    autoComplete="email"
                    value={loginEmail}
                    onChange={(ev) => setLoginEmail(ev.target.value)}
                    disabled={loginDisabled}
                  />
                </label>
                <label>
                  Sifre
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={loginPassword}
                    onChange={(ev) => setLoginPassword(ev.target.value)}
                    disabled={loginDisabled}
                  />
                </label>
                {loginError ? <div className="checkin-scanned-warn">{loginError}</div> : null}
                <button type="submit" className="checkin-btn" disabled={loginDisabled}>
                  {loginLoading || authBusy
                    ? authBusy && !loginLoading
                      ? 'Kayit aliniyor...'
                      : 'Giris...'
                    : staticFlow
                      ? 'Giris yap ve kaydet'
                      : 'Giris yap'}
                </button>
              </form>
            )}
            {baristaSignedIn && legacyToken && preview?.ok && authBusy ? (
              <p className="checkin-card-sub" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                Shift girisi kaydediliyor...
              </p>
            ) : null}
            {baristaSignedIn && staticFlow && authBusy ? (
              <p className="checkin-card-sub" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                Kaydediliyor...
              </p>
            ) : null}

            {result ? (
              <div className={`checkin-result ${result.ok ? 'ok' : 'err'}`} style={{ marginTop: '1rem' }}>
                {result.ok ? (
                  <div>
                    <div><strong>Kayit basarili</strong></div>
                    {result.checked_in_at != null ? (
                      <div>{new Date(result.checked_in_at).toLocaleString('tr-TR')}</div>
                    ) : (
                      <div>{result.day || '-'} · {result.shift_time || '-'}</div>
                    )}
                    <div style={{ marginTop: '0.75rem' }}>
                      <Link to="/barista">Vardiyalarima git</Link>
                    </div>
                  </div>
                ) : result.reason === 'already_used' ? (
                  <div>Bu QR kod daha once kullanildi.</div>
                ) : result.reason === 'not_found' ? (
                  <div>QR kod gecersiz.</div>
                ) : result.reason === 'not_barista' ? (
                  <div>Bu hesap barista degil.</div>
                ) : result.reason === 'admin_not_allowed' ? (
                  <div>Isletme sahibi hesabiyla check-in yapilmaz.</div>
                ) : (
                  <div>Hata: {typeof result.reason === 'string' ? result.reason : JSON.stringify(result)}</div>
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className="checkin-right" aria-hidden>
          <img src={dukkan} alt="" />
        </div>
      </div>
    </div>
  );
}
